import { Injectable, inject } from '@angular/core';

import { CookieService } from 'ngx-cookie-service';

import { AppStorageService } from '@core/services/app-storage.service';
import { environment } from '@env/environment';

import { SessionEndService } from './session-end.service';
import { UserSessionService } from './user-session.service';

/** When anyone last used the app in any tab, in epoch milliseconds; every tab shares it. */
export const LAST_ACTIVITY_KEY = 'pos.session.lastActivity';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  private cookieService = inject(CookieService);
  private sessionEnd = inject(SessionEndService);
  private userSession = inject(UserSessionService);
  private storage = inject(AppStorageService);

  private inactivityTimer: ReturnType<typeof setTimeout> | undefined;
  private eventListenersAdded = false;

  constructor() {
    this.trackUserActivity();
    this.startSessionTimer();
  }

  startSessionTimer() {
    const timeoutMinutes = this.userSession.sessionTimeoutMinutes();
    if (timeoutMinutes === null) {
      return;
    }

    const now = Date.now();
    const lastActivityTime = this.storage.get<number>('local', LAST_ACTIVITY_KEY, 0) || now;

    let elapsedMinutes = (now - lastActivityTime) / 60000;

    if (elapsedMinutes >= timeoutMinutes) {
      this.storage.set('local', LAST_ACTIVITY_KEY, now);
      elapsedMinutes = 0;
    }

    const remainingMinutes = timeoutMinutes - elapsedMinutes;
    clearTimeout(this.inactivityTimer);
    this.inactivityTimer = setTimeout(() => this.onTimerExpired(timeoutMinutes), remainingMinutes * 60000);
  }

  // In trackUserActivity, call startSessionTimer instead of startInactivityTimer
  trackUserActivity() {
    if (this.eventListenersAdded) return;
    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, () => {
        this.storage.set('local', LAST_ACTIVITY_KEY, Date.now());
        this.startSessionTimer();
      });
    });
    this.eventListenersAdded = true;
  }

  /**
   * Another tab may have been active since this tab's timer started: lastActivityTime is shared, so read it again
   * and only end the session when the whole app has been idle for the timeout.
   */
  private onTimerExpired(timeoutMinutes: number) {
    const lastActivityTime = this.storage.get<number>('local', LAST_ACTIVITY_KEY, 0);
    if (lastActivityTime && Date.now() - lastActivityTime < timeoutMinutes * 60000) {
      this.startSessionTimer();
      return;
    }
    this.logout();
  }

  logout() {
    this.sessionEnd.end(() => {
      const domain = environment.domainName;
      this.cookieService.deleteAll('/', domain);
      this.cookieService.set('logoutEvent', '1', 1 / 144, '/', domain); // 10 minutes expiry
      window.location.href = `${environment.companyWebBaseUrl}/shared/login`;
    });
  }
}
